import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { HeroGeometric } from "@/components/ui/shape-landing-hero";
import { CheckCircle2, Smartphone, Calendar, Image, Star, Zap, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SEOHead, getOrganizationSchema, getWebSiteSchema, getLocalBusinessSchema, getSoftwareApplicationSchema } from "@/components/SEOHead";
import { PortfolioShowcase } from "@/components/PortfolioShowcase";

const Index = () => {
  const { t, getLocalizedUrl, currentLanguage } = useLanguage();

  const features = [
    { icon: Smartphone, titleKey: "features.mobileResponsive", descKey: "features.mobileResponsiveDesc" },
    { icon: Calendar, titleKey: "features.updates", descKey: "features.updatesDesc" },
    { icon: Image, titleKey: "features.analytics", descKey: "features.analyticsDesc" },
    { icon: Star, titleKey: "features.support", descKey: "features.supportDesc" },
    { icon: Zap, titleKey: "features.sslSecure", descKey: "features.sslSecureDesc" },
    { icon: TrendingUp, titleKey: "features.seo", descKey: "features.seoDesc" },
  ];

  const businessTypes = [
    { key: "restaurant", labelKey: "businessTypes.restaurant" },
    { key: "salon", labelKey: "businessTypes.salon" },
    { key: "barber", labelKey: "businessTypes.barber" },
    { key: "foodTruck", labelKey: "businessTypes.foodTruck" },
    { key: "nail", labelKey: "businessTypes.nail" },
    { key: "other", labelKey: "businessTypes.other" },
  ];

  const included = [
    "features.mobileResponsive",
    "features.sslSecure",
    "features.seo",
    "features.analytics",
    "features.updates",
    "features.support",
  ];

  // Structured data for SEO
  const structuredData = [
    getOrganizationSchema(currentLanguage),
    getWebSiteSchema(currentLanguage),
    getLocalBusinessSchema(currentLanguage),
    getSoftwareApplicationSchema(currentLanguage, { ratingValue: 4.9, ratingCount: 127 })
  ];

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      <SEOHead 
        titleKey="seo.home.title"
        descriptionKey="seo.home.description"
        structuredData={structuredData}
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero Section with Geometric Shapes */}
        <HeroGeometric 
          badge={t("hero.badge")}
          title1={t("hero.title1")}
          title2={t("hero.title2")}
        />

        {/* Business Types Section */}
        <section className="py-20 bg-[#030303] relative">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.03] to-transparent" />
          <div className="container mx-auto px-4 relative z-10">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 text-white">
              {t("businessTypes.title")}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {businessTypes.map((type) => (
                <div
                  key={type.key}
                  className="p-6 text-center rounded-lg border border-white/[0.08] bg-white/[0.03] hover:border-indigo-500/30 hover:bg-white/[0.05] transition-all"
                >
                  <span className="font-medium text-white/90">{t(type.labelKey)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-[#030303] relative">
          <div className="absolute inset-0 bg-gradient-to-br from-rose-500/[0.03] via-transparent to-violet-500/[0.03]" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                {t("howItWorks.title")}
              </h2>
              <p className="text-lg text-white/40 max-w-2xl mx-auto">
                {t("howItWorks.description")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  step: "1",
                  titleKey: "howItWorks.step1.title",
                  descKey: "howItWorks.step1.description"
                },
                {
                  step: "2",
                  titleKey: "howItWorks.step2.title",
                  descKey: "howItWorks.step2.description"
                },
                {
                  step: "3",
                  titleKey: "howItWorks.step3.title",
                  descKey: "howItWorks.step3.description"
                }
              ].map((item) => (
                <Card key={item.step} className="relative border-white/[0.08] bg-white/[0.03] shadow-lg hover:border-indigo-500/30 transition-all">
                  <CardContent className="p-6">
                    <div className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-300 to-rose-300 mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-white">{t(item.titleKey)}</h3>
                    <p className="text-white/40">{t(item.descKey)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <Button asChild size="lg" variant="outline-light">
                <Link to={getLocalizedUrl("/how-it-works")}>
                  {t("howItWorks.cta")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 relative bg-[#030303]">
          <div className="absolute inset-0 bg-gradient-to-t from-indigo-500/[0.03] to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                {t("features.title")}
              </h2>
              <p className="text-lg text-white/40 max-w-2xl mx-auto">
                {t("features.description")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.titleKey}
                    className="group border-white/[0.08] bg-white/[0.03] backdrop-blur-sm hover:border-indigo-500/30 hover:bg-white/[0.05] transition-all duration-300"
                  >
                    <CardContent className="p-6">
                      <div className="mb-4 inline-flex p-3 bg-gradient-to-br from-indigo-500/20 to-rose-500/20 rounded-lg border border-white/[0.08]">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-white">{t(feature.titleKey)}</h3>
                      <p className="text-white/40">{t(feature.descKey)}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Portfolio Showcase Section */}
        <section className="py-20 relative bg-[#030303]">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/[0.05] via-transparent to-rose-500/[0.05] blur-3xl" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              {/* Portfolio Showcase */}
              <PortfolioShowcase />
            </div>
          </div>
        </section>

        {/* Your Control Center Section */}
        <section className="py-20 relative bg-[#030303]">
          <div className="absolute inset-0 bg-gradient-to-t from-indigo-500/[0.05] to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  {t("dashboard.title")}
                </h2>
                <p className="text-lg text-white/40">
                  {t("dashboard.description")}
                </p>
              </div>

              <div className="relative">
                <img
                  src="/portfolio/admin-dashboard-mockup.png"
                  alt={t("dashboard.title")}
                  width={1920}
                  height={1080}
                  loading="lazy"
                  decoding="async"
                  className="rounded-lg border border-white/[0.08] shadow-2xl w-full h-auto"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-12">
                {[
                  { icon: Sparkles, textKey: "features.updates" },
                  { icon: TrendingUp, textKey: "features.analytics" },
                  { icon: Smartphone, textKey: "features.support" }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <div className="p-2 bg-gradient-to-br from-indigo-500/20 to-rose-500/20 rounded-lg border border-white/[0.08]">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <p className="text-sm text-white/40">{t(item.textKey)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Section */}
        <section className="py-20 bg-[#030303] relative">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-indigo-500/[0.03]" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  {t("features.title")}
                </h2>
                <p className="text-lg text-white/40">
                  {t("features.description")}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {included.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 p-4 rounded-lg bg-white/[0.03] border border-white/[0.08] hover:border-indigo-500/30 transition-all"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500/20 to-rose-500/20 flex items-center justify-center">
                      <CheckCircle2 className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium text-white/90">{t(item)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 relative bg-[#030303] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.08] via-transparent to-rose-500/[0.08] blur-3xl" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                {t("cta.title")}
              </h2>
              <p className="text-xl text-white/40">
                {t("cta.description")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-indigo-500 to-rose-500 hover:from-indigo-600 hover:to-rose-600 border-0">
                  <Link to={getLocalizedUrl("/pricing")} className="flex items-center gap-2">
                    {t("cta.button")}
                    <Zap className="h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild variant="outline-light" size="lg">
                  <Link to={getLocalizedUrl("/how-it-works")}>
                    {t("howItWorks.cta")}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
